import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Help, Check, ArrowL, ArrowR, Facebook, GoogleIcon } from '../Icons/CustomIcons';
import { IconsLAccent01 } from '../Icons/Icons_L';
import { Advice } from '../Common/Card';


import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../services/api';
import { Input } from './Input';
import { PasswordInput } from './PasswordInput';
import { Button } from './Button';
import AddressInput from './AddressInput';
import { FormLabel } from './Dialog';

/* ─────────────────────────────────────────────
   Social-Login-Buttons (Google & Facebook)
───────────────────────────────────────────── */
const SocialLoginButtons = ({ returnTo = '/' }) => {
  const handleLogin = (provider) => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.location.href = `${apiBase}/auth/oauth/${provider}?von=${encodeURIComponent(returnTo)}`;
  };

  return (
    <div className="ml-social-login">
      <div className="ml-social-divider">
        <span className="ml-social-divider-text">Oder anmelden mit</span>
      </div>
      <div className="ml-social-buttons">
        <button type="button" className="body Font-M-I-120-D ml-social-btn" onClick={() => handleLogin('google')}>
          <GoogleIcon size={24} aria-hidden="true" />
          Google
        </button>
        <button type="button" className="body Font-M-I-120-D ml-social-btn" onClick={() => handleLogin('facebook')}>
          <Facebook size={24} aria-hidden="true" />
          Facebook
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Anmelden-Tab
   onSuccess: Callback nach erfolgreichem Login
   returnTo:  Fallback-Pfad wenn kein onSuccess
───────────────────────────────────────────── */
const LoginTab = ({ onSuccess, returnTo = '/' }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [resendStatus, setResendStatus] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!credentials.email || !credentials.password) return;
    setUnverifiedEmail(null);
    setResendStatus('');
    try {
      await login(credentials);
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(returnTo);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.email_unverified) {
        setUnverifiedEmail(err.response.data.email || credentials.email);
        setError('');
      } else {
        setError('Login fehlgeschlagen. Bitte überprüfen Sie Ihre Angaben.');
      }
    }
  };

  const handleResend = async () => {
    setResendStatus('');
    try {
      await authAPI.resendVerification(unverifiedEmail);
      setResendStatus('Bestätigungs-E-Mail wurde erneut gesendet. Prüfen Sie Ihr Postfach.');
    } catch {
      setResendStatus('Fehler beim Senden. Bitte versuchen Sie es später erneut.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="ml-form login-form" noValidate>
      {error && (
        <div className="error-message" role="alert">{error}</div>
      )}
      {unverifiedEmail && (
        <div className="ml-unverified" role="alert">
          <p>Ihre E-Mail-Adresse wurde noch nicht bestätigt.</p>
          <p>Bitte prüfen Sie Ihr Postfach (<strong>{unverifiedEmail}</strong>) und klicken Sie auf den Bestätigungslink.</p>
          {resendStatus
            ? <p className="ml-resend-ok">{resendStatus}</p>
            : (
              <button type="button" className="ml-resend-btn" onClick={handleResend}>
                Bestätigungs-E-Mail erneut senden
              </button>
            )
          }
        </div>
      )}

      <div className="ml-field">
        <FormLabel htmlFor="ap-email">E-Mail-Adresse</FormLabel>
        <Input
          id="ap-email"
          type="email"
          placeholder="ihr@email.com"
          value={credentials.email}
          onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
          className={submitted && !credentials.email ? 'input--error' : ''}
        />
      </div>

      <div className="ml-field">
        <FormLabel htmlFor="ap-password">Passwort</FormLabel>
        <PasswordInput
          id="ap-password"
          value={credentials.password}
          onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
          className={submitted && !credentials.password ? 'input--error' : ''}
        />
      </div>

      <button
        type="button"
        className="ml-forgot"
        onClick={() => navigate('/passwort-vergessen')}
      >
        <Help size={16} aria-hidden="true" />
        Passwort vergessen?
      </button>

      <Button type="submit" className="btn-secondary w-full">
        Anmelden <ArrowR size={28} aria-hidden="true" />
      </Button>

      <SocialLoginButtons returnTo={returnTo} />
    </form>
  );
};

/* ─────────────────────────────────────────────
   Registrieren-Tab
───────────────────────────────────────────── */
const RegisterTab = ({ onRegistered }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    vorname: '',
    name: '',
    username: '',
    email: '',
    password: '',
    strasse: '',
    plz: '',
    wohnort: '',
  });
  const [step1Submitted, setStep1Submitted] = useState(false);
  const [step2Submitted, setStep2Submitted] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const addressValidateRef = useRef(null);

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.strasse.trim()) {
      const addrErr = await addressValidateRef.current?.();
      if (addrErr) { setError(addrErr); return; }
    }
    try {
      await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        vorname: formData.vorname,
        name: formData.name,
        strasse: formData.strasse,
        plz: formData.plz,
        wohnort: formData.wohnort,
      });
      onRegistered(formData.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    }
  };

  return (
    <div className="register-tab">
      <h2 className="headings Font-H2-S-120-T">Erstellen Sie ein Konto</h2>
      <form onSubmit={handleSubmit} className="ml-form register-form">
        {error && <div className="error-message" role="alert">{error}</div>}

        {/* Schritt 1: Persönliche Daten */}
        {step === 1 && (
          <>
            <p className="step-info">Schritt 1 von 3: Persönliche Daten</p>
            <div className="ml-input-row">
              <div className="ml-name-row">
                <div className="ml-field">
                  <FormLabel htmlFor="ap-vorname">Vorname</FormLabel>
                  <Input id="ap-vorname" type="text" placeholder="Max"
                    value={formData.vorname} onChange={set('vorname')}
                    className={step1Submitted && !formData.vorname.trim() ? 'input--error' : ''} />
                </div>
                <div className="ml-field">
                  <FormLabel htmlFor="ap-nachname">Name</FormLabel>
                  <Input id="ap-nachname" type="text" placeholder="Mustermann"
                    value={formData.name} onChange={set('name')}
                    className={step1Submitted && !formData.name.trim() ? 'input--error' : ''} />
                </div>
              </div>
              <div className="ml-field">
                <FormLabel htmlFor="ap-username">Benutzername</FormLabel>
                <Input id="ap-username" type="text" placeholder="max_m"
                  value={formData.username} onChange={set('username')}
                  className={step1Submitted && !formData.username.trim() ? 'input--error' : ''} />
              </div>
            </div>
            <Button type="button" className="btn btn-primary w-full" onClick={() => {
              setStep1Submitted(true);
              if (formData.vorname.trim() && formData.name.trim() && formData.username.trim()) setStep(2);
            }}>
              Weiter: E-Mail-Adresse <ArrowR size={20} aria-hidden="true" />
            </Button>
          </>
        )}

        {/* Schritt 2: E-Mail-Adresse und Passwort */}
        {step === 2 && (() => {
          const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
          const pwValid = formData.password.length >= 8 && /[A-Z]/.test(formData.password) && /[0-9]/.test(formData.password);
          const step2Ok = emailValid && pwValid;
          return (
            <>
              <p className="step-info">Schritt 2 von 3: E-Mail-Adresse & Passwort</p>
              <div className="ml-field">
                <FormLabel htmlFor="ap-reg-email">E-Mail-Adresse</FormLabel>
                <Input id="ap-reg-email" type="email" placeholder="max.mustermann@email.com"
                  value={formData.email} onChange={set('email')} required
                  className={step2Submitted && !emailValid ? 'input--error' : ''} />
                {step2Submitted && !emailValid && (
                  <p className="text-xs Colors-Red mt-1">Bitte eine gültige E-Mail-Adresse eingeben.</p>
                )}
              </div>
              <div className="ml-field">
                <FormLabel htmlFor="ap-reg-password">Passwort</FormLabel>
                <PasswordInput id="ap-reg-password"
                  value={formData.password} onChange={set('password')} required
                  className={step2Submitted && !pwValid ? 'input--error' : ''} />
                {step2Submitted && !pwValid && (
                  <p className="text-xs Colors-Red mt-1">Mindestens 8 Zeichen, ein Grossbuchstabe und eine Zahl.</p>
                )}
              </div>  
                       
              <Advice text="Bitte geben Sie Ihr Passwort sorgfältig ein. Sie können es jederzeit ändern." />
              <div className="flex gap-3 mt-4">
                <button type="button" className="reg-step-back-btn" onClick={() => setStep(1)} aria-label="Zurück">
                  <ArrowL size={20} color="var(--Colors-Accent-01)" aria-hidden="true" />
                </button>
                <Button type="button" className="flex-1" onClick={() => { setStep2Submitted(true); if (step2Ok) setStep(3); }}>
                  Start-Adresse <ArrowR size={20} aria-hidden="true" />
                </Button>
              </div>
            </>
          );
        })()}

        {/* Schritt 3: Adresse */}
        {step === 3 && (
          <>
            <p className="step-info">Schritt 3 von 3: Adresse (Optional)</p>
            <AddressInput
              street={formData.strasse}
              plz={formData.plz}
              city={formData.wohnort}
              onChange={({ street, plz, city }) =>
                setFormData(prev => ({ ...prev, strasse: street, plz, wohnort: city }))
              }
              validateRef={addressValidateRef}
              className="address-input--register"
            />
            <div className="flex gap-3 mt-4">
              <button type="button" className="reg-step-back-btn" onClick={() => setStep(2)} aria-label="Zurück">
                <ArrowL size={20} color="var(--Colors-Accent-01)" aria-hidden="true" />
              </button>
              <Button type="submit" className="flex-1">
                <Check size={20} aria-hidden="true" /> Registrieren
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

/* ─────────────────────────────────────────────
   AuthPanel
   Gemeinsame Komponente für Seite und Dialog.

   Props:
   - onSuccess   : Callback nach Login (Dialog-Modus)
   - returnTo    : Pfad nach Login (Seiten-Modus, default '/')
   - onBack      : Callback für Zurück-Button (undefined = kein Button)
   - initialTab  : 'anmelden' | 'registrieren'
───────────────────────────────────────────── */
const AuthPanel = ({
  onSuccess,
  returnTo = '/',
  onBack,
  initialTab = 'anmelden',
  hideHero = false,
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [registeredEmail, setRegisteredEmail] = useState(null);
  const [resendStatus, setResendStatus] = useState('');

  const handleResend = async () => {
    setResendStatus('');
    try {
      await authAPI.resendVerification(registeredEmail);
      setResendStatus('E-Mail wurde erneut gesendet. Bitte prüfen Sie Ihr Postfach.');
    } catch {
      setResendStatus('Fehler beim Senden. Bitte versuchen Sie es später erneut.');
    }
  };

  const handleBackToLogin = () => {
    setRegisteredEmail(null);
    setResendStatus('');
    setTab('anmelden');
    navigate('/anmelden');
  };

  return (
    <>
    <div className="auth-panel">
      {/* Hero – nur wenn nicht im Fullpage-Modus */}
      {!hideHero && (
        <div className="auth-panel-hero">
          {onBack && (
            <button
              type="button"
              className="mobile-login-back"
              onClick={onBack}
              aria-label="Zurück"
            >
              <ArrowL size={20} aria-hidden="true" />
              Zurück
            </button>
          )}
          <h2 className="auth-panel-hero-title">Konto erstellen oder anmelden</h2>
          <p className="auth-panel-hero-subtitle">Melden Sie sich an, um Aktivitäten eingeben zu können</p>
        </div>
      )}

      {/* Tabs + Formular */}
      <div className="auth-panel-body">
        {hideHero && onBack && (
          <button
            type="button"
            className="auth-panel-back-btn"
            onClick={onBack}
            aria-label="Zurück"
          >
            <ArrowL size={16} aria-hidden="true" />
            Zurück
          </button>
        )}
        <div className="address-coord-section" role="tablist" aria-label="Anmelden oder Registrieren">
          <Button
            type="button"
            role="tab"
            aria-selected={tab === 'anmelden'}
            variant={tab === 'anmelden' ? 'default' : 'outline'}
            className="address-coord-btn"
            onClick={() => setTab('anmelden')}
          >
            Anmelden
          </Button>
          <Button
            type="button"
            role="tab"
            aria-selected={tab === 'registrieren'}
            variant={tab === 'registrieren' ? 'default' : 'outline'}
            className="address-coord-btn"
            onClick={() => setTab('registrieren')}
          >
            Registrieren
          </Button>
        </div>

        <div className="mobile-login-content">
          {tab === 'anmelden'
            ? <LoginTab onSuccess={onSuccess} returnTo={returnTo} />
            : <RegisterTab onRegistered={setRegisteredEmail} />
          }
        </div>
      </div>
    </div>

      {/* Erfolgs-Popup nach Registrierung */}
      {registeredEmail && (
        <div className="auth-success-overlay" role="dialog" aria-modal="true" aria-label="Registrierung erfolgreich">
          <div className="auth-success-popup">
            <div className="ml-success" role="status"><div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
              <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconsLAccent01 size={84} />
                  <Check size={28} color="#fff" style={{ position: 'absolute' }} />
                </div>
              </div>
              <h2 className="ml-success-title">Fast geschafft!</h2>
              <p>Wir haben eine Bestätigungs-E-Mail an <strong>{registeredEmail}</strong> gesendet.<br />
                Bitte klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.</p>
              <p className="ml-success-hint">Keine E-Mail erhalten? Prüfen Sie Ihren Spam-Ordner.</p>
              {resendStatus && <p className="ml-resend-ok">{resendStatus}</p>}
              <Button variant="outline" className="w-full" onClick={handleResend}>
                Bestätigungs-E-Mail erneut senden
              </Button>
              <Button className="w-full" onClick={handleBackToLogin}>
                <ArrowL size={28} aria-hidden="true" style={{ marginRight: '0.5rem' }} />
                Zur Anmeldung
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthPanel;